// A1-105 (a) — the return boxes each shown row fills (api/routes_row_boxes).
// Every card that asks in the same moment shares one request (cards of up to
// 10 rows open by themselves, so a page opens many at once), each row is
// asked once per calculation, and a card re-opened reads what it already has.
// An answer can change rows without a new calculation, so every answer forgets
// what was read (forgetRowBoxes) and the shown cards ask again.
// A failed read shows nothing: the line is a help, never a blocker.
import { useEffect, useState } from "react";
import { authClient } from "../../lib/authApi";

export const ROW_BOXES_PAGE = 200;   // the route's limit

const known = { calcId: null, rows: new Map() };   // txn id -> Promise<boxes>
let queue = [];                                    // [{calcId, tid, resolve}]
let revision = 0;
const shown = new Set();                           // the mounted hooks' setters

export const forgetRowBoxes = () => {
  known.rows = new Map();
  revision += 1;
  shown.forEach((ask) => ask(revision));
};

const send = (calcId, part) => {
  const qs = part.map((p) => `txn_ids=${encodeURIComponent(p.tid)}`).join("&");
  Promise.resolve()
    .then(() => authClient.get(`/runs/calc/${calcId}/row-boxes?${qs}`))
    .then((res) => { const rows = res?.data?.rows || {}; part.forEach((p) => p.resolve(rows[p.tid] || [])); })
    .catch(() => part.forEach((p) => {
      if (known.calcId === calcId) known.rows.delete(p.tid);   // asked again next time
      p.resolve([]);
    }));
};

const flush = () => {
  const batch = queue;
  queue = [];
  const byCalc = {};
  batch.forEach((p) => { (byCalc[p.calcId] = byCalc[p.calcId] || []).push(p); });
  Object.entries(byCalc).forEach(([calcId, items]) => {
    for (let i = 0; i < items.length; i += ROW_BOXES_PAGE) send(calcId, items.slice(i, i + ROW_BOXES_PAGE));
  });
};

export const boxesFor = (calcId, tid) => {
  if (known.calcId !== calcId) { known.calcId = calcId; known.rows = new Map(); }
  if (!known.rows.has(tid)) {
    known.rows.set(tid, new Promise((resolve) => {
      if (!queue.length) setTimeout(flush, 0);
      queue.push({ calcId, tid, resolve });
    }));
  }
  return known.rows.get(tid);
};

export const useRowBoxes = (calcId, tids, enabled) => {
  const [got, setGot] = useState({ calcId: null, rows: {} });
  const [rev, setRev] = useState(revision);
  useEffect(() => { shown.add(setRev); return () => { shown.delete(setRev); }; }, []);
  const key = enabled && calcId ? (tids || []).join(",") : "";
  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    const ids = key.split(",");
    Promise.all(ids.map((tid) => boxesFor(calcId, tid))).then((list) => {
      if (live) setGot({ calcId, rows: Object.fromEntries(ids.map((tid, i) => [tid, list[i]])) });
    });
    return () => { live = false; };
  }, [calcId, key, rev]);
  return got.calcId === calcId ? got.rows : {};   // never another calculation's boxes
};

export default useRowBoxes;
